import { HeatQueueEntry } from "src/store/api/generatedApi";

const heatIndexByStableId = (heats: HeatQueueEntry[]): Map<string, number> =>
  new Map(heats.map((heat, index) => [heat.stable_heat_id, index]));

export const resolveContiguousSelection = ({
  heats,
  candidateStableHeatIds,
  eligibleStableHeatIds,
  maxHeats,
}: {
  heats: HeatQueueEntry[];
  candidateStableHeatIds: string[];
  eligibleStableHeatIds: Set<string>;
  maxHeats: number | null;
}): string[] => {
  const indexById = heatIndexByStableId(heats);
  const candidateIndices = candidateStableHeatIds
    .filter((stableHeatId) => eligibleStableHeatIds.has(stableHeatId))
    .map((stableHeatId) => indexById.get(stableHeatId))
    .filter((index): index is number => index !== undefined)
    .sort((a, b) => a - b);

  const firstIndex = candidateIndices[0];
  if (firstIndex === undefined) {
    return [];
  }
  const mixId = heats[firstIndex]?.floor_recipe?.mix.id;
  if (mixId === undefined) {
    return [];
  }

  const run: string[] = [];
  let expectedIndex = firstIndex;
  for (const index of candidateIndices) {
    const heat = heats[index];
    if (heat === undefined) {
      break;
    }
    if (index !== expectedIndex || heat.floor_recipe?.mix.id !== mixId) {
      break;
    }
    run.push(heat.stable_heat_id);
    expectedIndex += 1;
  }
  return maxHeats === null ? run : run.slice(0, maxHeats);
};

export const selectableStableHeatIds = ({
  heats,
  selectedStableHeatIds,
  eligibleStableHeatIds,
  maxHeats,
}: {
  heats: HeatQueueEntry[];
  selectedStableHeatIds: string[];
  eligibleStableHeatIds: Set<string>;
  maxHeats: number | null;
}): Set<string> => {
  const indexById = heatIndexByStableId(heats);
  const selectedIndices = selectedStableHeatIds
    .map((stableHeatId) => indexById.get(stableHeatId))
    .filter((index): index is number => index !== undefined);

  if (selectedIndices.length === 0) {
    return new Set(
      heats
        .filter(
          (heat) =>
            eligibleStableHeatIds.has(heat.stable_heat_id) &&
            heat.floor_recipe !== null
        )
        .map((heat) => heat.stable_heat_id)
    );
  }

  const selectable = new Set<string>();
  const loHeat = heats[Math.min(...selectedIndices)];
  const hiHeat = heats[Math.max(...selectedIndices)];
  if (loHeat === undefined || hiHeat === undefined) {
    return selectable;
  }
  selectable.add(loHeat.stable_heat_id);
  selectable.add(hiHeat.stable_heat_id);

  const mixId = loHeat.floor_recipe?.mix.id;
  if (
    mixId === undefined ||
    (maxHeats !== null && selectedIndices.length >= maxHeats)
  ) {
    return selectable;
  }
  const before = heats[Math.min(...selectedIndices) - 1];
  if (
    before !== undefined &&
    eligibleStableHeatIds.has(before.stable_heat_id) &&
    before.floor_recipe?.mix.id === mixId
  ) {
    selectable.add(before.stable_heat_id);
  }
  const after = heats[Math.max(...selectedIndices) + 1];
  if (
    after !== undefined &&
    eligibleStableHeatIds.has(after.stable_heat_id) &&
    after.floor_recipe?.mix.id === mixId
  ) {
    selectable.add(after.stable_heat_id);
  }
  return selectable;
};
